const getTurnRatesPercentages = turns => {
    const leftTurnPercentages = [];
    const rightTurnPercentages = [];
    
    for (let i = 0; i < turns.length; i++) {
        const leftCounts = turns[i].leftCount;
        const rightCounts = turns[i].rightCount;
        const totalCounts = turns[i].totalCount;

        const getTurnPercentages = (turnCounts, totalCounts) => Math.round((turnCounts / totalCounts) * 100);
        leftTurnPercentages.push(getTurnPercentages(leftCounts, totalCounts));
        rightTurnPercentages.push(getTurnPercentages(rightCounts, totalCounts));
    }
    
    return { leftTurnPercentages, rightTurnPercentages };
};

const getTurnRatesLabels = (initialLabels, hasNormalTurnRange, hasZScores, useMatchTimeFilter) => {
        const labels = [];
        //add * only when player has zScores, match times and is outside normal range
        for (let i = 0; i < initialLabels.length; i++) {
            const checkedLabels = hasZScores && useMatchTimeFilter && hasNormalTurnRange[i] !== null ? initialLabels[i] + '*' : initialLabels[i];
            labels.push(checkedLabels);
        }
        return labels;
};

const getTurnRatesRanges = (turns, zScores, hasInsufficientMatchTime) => {
    const leftTurnNormalRange = [];
    const rightTurnNormalRange = [];

    for (let i = 0; i < turns.length; i++) {
        if (zScores) {
            const multipleZScores = zScores[i];
            const getBounds = category => (multipleZScores ? zScores[i][category] : zScores[0][category]);
            const leftTurnsLowerBound = getBounds('leftTurnsLowerBound');
            const leftTurnsUpperBound = getBounds('leftTurnsUpperBound');
            const rightTurnsLowerBound = getBounds('rightTurnsLowerBound');
            const rightTurnsUpperBound = getBounds('rightTurnsUpperBound');

            const getTurnRange = (percentages, lowerBound, upperBound) => {
                const roundedLowerBound = Math.round(lowerBound);
                const roundedUpperBound = Math.round(upperBound);
                const roundedPercentage = Math.round(percentages);

                // If player is within normal range or did not play the full game, then don't normal range
                return (roundedPercentage >= roundedLowerBound && roundedPercentage <= roundedUpperBound) || hasInsufficientMatchTime[i]
                    ? null
                    : roundedLowerBound + '%' + ' - ' + roundedUpperBound + '%';
            };

            const turnPercentages = getTurnRatesPercentages(turns);
            leftTurnNormalRange.push(getTurnRange(turnPercentages.leftTurnPercentages[i], leftTurnsLowerBound, leftTurnsUpperBound));
            rightTurnNormalRange.push(getTurnRange(turnPercentages.rightTurnPercentages[i], rightTurnsLowerBound, rightTurnsUpperBound));
        }
    }

    return { leftTurnNormalRange, rightTurnNormalRange };
};


const getSeasonTurnData = (sessionTurnClassCounts, seasonSelected) => {
	return sessionTurnClassCounts.filter((turn) =>
		seasonSelected.contains(new Date(turn.sessionDate))
	);
};
const getTurnsByDateRange = (range, data) => {
	//check that a date range has been set on the calendar picker
	if (range.start.toDateString() === new Date().toDateString()) {
		return data;
	} else {
		return data.filter((session) => {
			const setDateToMidnight = (date) =>
				new Date(new Date(date).setUTCHours(0, 0, 0, 0));
			const sessionDate = setDateToMidnight(session.sessionDate);
			return (
				sessionDate >= setDateToMidnight(range.start) &&
				sessionDate <= setDateToMidnight(range.end)
			);
		});
	}
};
const getSessionTurns = (longitudinalView, seasonData, allSessions, range) => {
	return longitudinalView
		? getTurnsByDateRange(range, seasonData)
		: allSessions;
};

const extractTurnInfo = (sessionTurns, selectedTurnClass) => {
	const turns = [];
	const sessionDates = [];
	const tooltipTitles = [];

	for (const session of sessionTurns) {
		const selectedClass =
			selectedTurnClass === "byTotalTurns"
				? session.turnClassCounts.byAngleClass
				: session.turnClassCounts[selectedTurnClass];

		turns.push(selectedClass);
		sessionDates.push(session.sessionDate);
		tooltipTitles.push(session.title);
	}

	return { turns, sessionDates, tooltipTitles };
};

const getFilterOptions = (
	selectedTurnClass,
	{
		speedSelected,
		anglesSelected,
		accelerationsSelected,
		decelerationsSelected,
	}
) => {
	const turnClasses = {
		byTotalTurns: null,
		byEntrySpeed: speedSelected,
		byAngleClass: anglesSelected,
		byAccelerationPeakClass: accelerationsSelected,
		byDecelerationPeakClass: decelerationsSelected,
	};
	return turnClasses[selectedTurnClass];
};

const getClassificationNames = (
	selectedTurnClass,
	turnEntrySpeedBoundaries
) => {
	return selectedTurnClass === "byEntrySpeed"
		? turnEntrySpeedBoundaries.names
		: ["Low", "Medium", "High"];
};

const getFilteredTurnCounts = (
	turns,
	filterOptions,
	classificationNames,
	longitudinalView
) => {
	return turns.map((turn) => {
		if (!longitudinalView || !filterOptions?.length) return turn;

		const result = {
			leftClassificationCounts: {},
			rightClassificationCounts: {},
			combinedClassificationCounts: {},
		};

		classificationNames.forEach((name) => {
			result.leftClassificationCounts[name] = 0;
			result.rightClassificationCounts[name] = 0;
			result.combinedClassificationCounts[name] = 0;
		});

		filterOptions.forEach((i) => {
			const name = classificationNames[i];
			result.leftClassificationCounts[name] =
				turn.leftClassificationCounts[name];
			result.rightClassificationCounts[name] =
				turn.rightClassificationCounts[name];
			result.combinedClassificationCounts[name] =
				turn.combinedClassificationCounts[name];
		});
		return { ...turn, ...result };
	});
};

const determineTurnType = (selectedTurnClass, selectedTurnSide) => {
	if (selectedTurnSide === "leftRight") {
		return selectedTurnClass !== "byTotalTurns"
			? { type: "DEFAULT" }
			: { type: "SPLIT_TOTAL" };
	} else {
		return selectedTurnClass !== "byTotalTurns"
			? { type: "GROUPED_TOTAL" }
			: { type: "TOTAL" };
	}
};

const calculateTotalCounts = (turns) => {
	return turns.reduce(
		(acc, item) => {
			acc.left += item.leftCount;
			acc.right += item.rightCount;
			acc.total += item.totalCount;
			return acc;
		},
		{ left: 0, right: 0, total: 0 }
	);
};


export {
	getTurnRatesPercentages,
	getTurnRatesRanges,
	getTurnRatesLabels,
	getSeasonTurnData,
	getSessionTurns,
	extractTurnInfo,
	getFilterOptions,
	getClassificationNames,
	getFilteredTurnCounts,
	determineTurnType,
	calculateTotalCounts,
	getTurnsByDateRange,
};